/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { UserAuth } from "../context/authContext";
import { clsx } from "clsx";
import toast from "react-hot-toast";
import { animated, useSpring } from "react-spring";
import { FaRegClipboard, FaWallet, FaPlus, FaSignInAlt, FaHome, FaChartLine } from "react-icons/fa";
import { FiMenu, FiX, FiLogOut, FiSettings, FiGrid } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import JoinRoom from "../components/JoinRoom";
import CreateRoom from "../components/CreateRoom";
import logo from "./assets/astra_hack_logo.png";

import MainLayout from "./Layouts/MainLayout";

const Dashboard = () => {
  const { userData, user } = UserAuth();

  return (
    <MainLayout activeId="dashboard">
      <div className="min-h-screen bg-background text-text-main font-sans selection:bg-primary selection:text-white">
        {userData && <Content user={user} userData={userData} />}
      </div>
    </MainLayout>
  );
};

function Content({ user, userData, onSidebarShow }) {
  const milliseconds =
    userData?.createdAt?.seconds * 1000 + userData?.createdAt?.nanoseconds / 1000000 || Date.now();
  const dateObject = new Date(milliseconds);
  const formattedDate = dateObject.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const updateWallet = async () => {
    if (userData && userData.isWalletCreated) {
      toast.error("Wallet already created");
      return;
    }
    try {
      await db.collection("users").doc(user.email).update({
        isWalletCreated: true,
      });
      toast.success("Wallet Created Successfully");
      window.location.reload();
    } catch (e) {
      toast.error("Error creating wallet");
    }
  };

  const copyToClipboard = (data) => {
    navigator.clipboard.writeText(data).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Failed to copy")
    );
  };

  const fadeIn = useSpring({
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
    config: { tension: 280, friction: 60 },
  });

  return (
    <main className="flex-1 overflow-y-auto overflow-x-hidden">
      {/* Header Mobile */}
      <div className="sm:hidden flex items-center justify-between mb-4">
        <button onClick={onSidebarShow} className="p-2 glass-card text-white">
          <FiMenu size={24} />
        </button>
        <span className="font-bold text-lg">Dashboard</span>
        <div className="w-10" />
      </div>

      <animated.div style={fadeIn} className="w-full space-y-4">
        {/* Helper Top Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 p-4 md:p-5 glass-card relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />

          <div className="relative z-10">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Welcome back,{" "}
              <span className="gradient-text">{user?.email?.split("@")[0] || "User"}</span>
            </h1>
            <p className="text-text-muted flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Member since {formattedDate}
            </p>
          </div>

          {/* Wallet Status Badge */}
          <div className="relative z-10">
            {userData?.isWalletCreated ? (
              <span className="px-4 py-2 rounded-full bg-green-500/10 text-green-400 border border-green-500/20 text-sm font-medium flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" /> Wallet Active
              </span>
            ) : (
              <span className="px-4 py-2 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 text-sm font-medium flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full" /> No Wallet
              </span>
            )}
          </div>
        </div>

        {/* Wallet Section */}
        <div className="space-y-6">
          {/* Credit Card UI */}
          <div className="glass-card p-8 relative overflow-hidden group min-h-[220px] flex flex-col justify-between transition-all hover:shadow-[0_0_40px_rgba(99,102,241,0.2)]">
            <div className="absolute inset-0 bg-gradient-to-br from-surface to-background opacity-90" />
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-primary/20 blur-[100px] rounded-full group-hover:bg-primary/30 transition-all duration-700" />
            <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-secondary/20 blur-[100px] rounded-full group-hover:bg-secondary/30 transition-all duration-700" />

            <div className="relative z-10 flex justify-between items-start">
              <div>
                <p className="text-text-muted text-sm font-medium tracking-wider uppercase mb-1">
                  Total Balance
                </p>
                <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
                  {(userData?.balance || 0).toFixed(2)}{" "}
                  <span className="text-2xl text-primary">COINS</span>
                </h2>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-md border border-white/10">
                <FaWallet className="text-white" />
              </div>
            </div>

            <div className="relative z-10 mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-6 justify-between items-end">
              {userData?.isWalletCreated ? (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                  <div className="bg-black/30 p-3 rounded-lg border border-white/5 transition-colors hover:border-white/20">
                    <p className="text-xs text-text-muted mb-1">Public Key</p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm text-white truncate font-mono flex-1">
                        {userData.publicKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(userData.publicKey)}
                        className="text-text-muted hover:text-white p-1"
                      >
                        <FaRegClipboard />
                      </button>
                    </div>
                  </div>
                  <div className="bg-black/30 p-3 rounded-lg border border-white/5 transition-colors hover:border-white/20">
                    <p className="text-xs text-text-muted mb-1">Private Key</p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm text-white truncate font-mono flex-1">
                        {userData.privateKey?.substring(0, 10)}••••••••
                      </code>
                      <button
                        onClick={() => copyToClipboard(userData.privateKey)}
                        className="text-text-muted hover:text-white p-1"
                      >
                        <FaRegClipboard />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={updateWallet}
                  className="btn-primary flex items-center gap-2 shadow-lg hover:shadow-primary/50"
                >
                  Create Wallet
                </button>
              )}
            </div>
          </div>

          {/* Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card p-6 hover:bg-white/5 transition-colors duration-300">
              <CreateRoom />
            </div>
            <div className="glass-card p-6 hover:bg-white/5 transition-colors duration-300">
              <JoinRoom />
            </div>
          </div>
        </div>
      </animated.div>
    </main>
  );
}

export default Dashboard;
